import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useKanbanSensors } from './useKanbanSensors'
import type { KanbanCardMoveEvent, KanbanColumnData } from './types'

interface Props<TCard extends { id: string }> {
  columns: KanbanColumnData<TCard>[]
  onCardMove: (event: KanbanCardMoveEvent) => void | Promise<void>
  /** Optional — when provided, column-level drag-to-reorder is enabled. */
  onColumnReorder?: (orderedColumnIds: string[]) => void | Promise<void>
  /** Render each column. Receive both the column data and a stable boolean for whether column reordering is enabled. */
  renderColumn: (column: KanbanColumnData<TCard>, sortable: boolean) => ReactNode
  /** Optional trailing slot rendered after all columns (e.g. an "Add column" button). */
  trailingSlot?: ReactNode
  className?: string
}

/**
 * Generic Kanban board. Owns:
 * - DndContext + sensors
 * - Horizontal scroll layout
 * - SortableContext for column reordering (when enabled)
 * - The drag-end algorithm that distinguishes column-drag from card-drag and
 *   resolves the target column + insertion index for moved cards
 *
 * It deliberately does NOT own column or card JSX — consumers render those via
 * `renderColumn` + their own `KanbanColumn`/`KanbanCard` usage.
 */
export function KanbanBoard<TCard extends { id: string }>({
  columns,
  onCardMove,
  onColumnReorder,
  renderColumn,
  trailingSlot,
  className,
}: Props<TCard>) {
  const sensors = useKanbanSensors()
  const sortable = Boolean(onColumnReorder)

  const columnIds = useMemo(() => columns.map((c) => c.id), [columns])

  // Index card → column membership for fast drag-end resolution.
  const { cardColumnIndex, cardListByColumn } = useMemo(() => {
    const cardColumnIndex = new Map<string, string>()
    const cardListByColumn = new Map<string, TCard[]>()
    for (const col of columns) {
      cardListByColumn.set(col.id, col.cards)
      for (const card of col.cards) cardColumnIndex.set(card.id, col.id)
    }
    return { cardColumnIndex, cardListByColumn }
  }, [columns])

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const isColumnDrag = columnIds.includes(activeId)

    if (isColumnDrag) {
      if (!onColumnReorder) return
      if (!columnIds.includes(overId)) return
      const fromIdx = columnIds.indexOf(activeId)
      const toIdx = columnIds.indexOf(overId)
      if (fromIdx === -1 || toIdx === -1) return
      const next = [...columnIds]
      const [moved] = next.splice(fromIdx, 1)
      if (!moved) return
      next.splice(toIdx, 0, moved)
      await onColumnReorder(next)
      return
    }

    // Card drag
    const fromColumnId = cardColumnIndex.get(activeId)
    if (!fromColumnId) return

    let toColumnId: string
    let toIndex: number

    if (columnIds.includes(overId)) {
      // Dropped on a column (empty zone or column body) — append
      toColumnId = overId
      const target = cardListByColumn.get(toColumnId) ?? []
      toIndex = target.filter((c) => c.id !== activeId).length
    } else {
      // Dropped on another card — insert at its index
      const overColumnId = cardColumnIndex.get(overId)
      if (!overColumnId) return
      toColumnId = overColumnId
      const target = cardListByColumn.get(toColumnId) ?? []
      const withoutActive = target.filter((c) => c.id !== activeId)
      toIndex = withoutActive.findIndex((c) => c.id === overId)
      if (toIndex === -1) toIndex = withoutActive.length
    }

    // No-op: dropped at its existing slot
    if (fromColumnId === toColumnId) {
      const sourceList = cardListByColumn.get(fromColumnId) ?? []
      const currentIdx = sourceList.findIndex((c) => c.id === activeId)
      if (currentIdx === toIndex) return
    }

    await onCardMove({ cardId: activeId, fromColumnId, toColumnId, toIndex })
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        <div
          className={
            className ??
            'flex gap-4 overflow-x-auto p-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
          }
        >
          {columns.map((col) => renderColumn(col, sortable))}
          {trailingSlot}
        </div>
      </SortableContext>
    </DndContext>
  )
}
